import React, { useState, useEffect } from 'react';
import type { Player, Court } from '../services/api';
import { getPlayers, getCourts, getMatches, createMatch } from '../services/api';
import eventBus from '../utils/eventBus';
import { Button, Box, Typography, Alert, CircularProgress, FormGroup, FormControlLabel, Checkbox, Select, MenuItem, InputLabel, FormControl, Divider, Paper } from '@mui/material';
import { useNotification } from '../context/NotificationContext';


interface ManualSchedulerProps {
  onClose: () => void;
}

const ManualScheduler: React.FC<ManualSchedulerProps> = ({ onClose }) => {
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [availableCourts, setAvailableCourts] = useState<Court[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([]); // Use array for order
  const [selectedCourtId, setSelectedCourtId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [playersData, courtsData, matchesData] = await Promise.all([
          getPlayers(),
          getCourts(),
          getMatches(true) // Active matches
        ]);

        const busyCourtIds = new Set(matchesData.map(m => m.court_id));
        setAvailableCourts(courtsData.filter(c => !busyCourtIds.has(c.id)));
        setAllPlayers(playersData);
        setError(null);
      } catch (_err) {
        console.error("Error loading data:", _err);
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePlayerSelection = (playerId: number) => {
    setError(null);
    const newSelectedIds = [...selectedPlayerIds];
    const playerIndex = newSelectedIds.indexOf(playerId);

    if (playerIndex > -1) {
      newSelectedIds.splice(playerIndex, 1);
    } else {
      if (newSelectedIds.length < 4) {
        newSelectedIds.push(playerId);
      } else {
        showNotification("最多只能選擇 4 位球員。", "warning");
      }
    }
    setSelectedPlayerIds(newSelectedIds);
  };

  const handleCreateMatch = async () => {
    if (selectedPlayerIds.length !== 4) {
      setError('請選擇剛好 4 位球員。');
      return;
    }
    if (!selectedCourtId) {
      setError('請選擇一個場地。');
      return;
    }

    const team1_ids = [selectedPlayerIds[0], selectedPlayerIds[1]];
    const team2_ids = [selectedPlayerIds[2], selectedPlayerIds[3]];

    const team1_players = team1_ids.map(id => allPlayers.find(p => p.id === id));
    const team2_players = team2_ids.map(id => allPlayers.find(p => p.id === id));

    if (team1_players.some(p => !p) || team2_players.some(p => !p)) {
        setError("計算隊伍積分時發生錯誤。");
        return;
    }

    const team1_rating = team1_players[0]!.rating_mu + team1_players[1]!.rating_mu;
    const team2_rating = team2_players[0]!.rating_mu + team2_players[1]!.rating_mu;
    const quality_score = 1.0 / (1.0 + Math.abs(team1_rating - team2_rating));

    try {
        setLoading(true);
        await createMatch({
            court_id: parseInt(selectedCourtId, 10),
            player_ids: selectedPlayerIds,
            teams: { team1: team1_ids, team2: team2_ids },
            quality_score: quality_score,
        });
        eventBus.dispatch('dataChanged');
        showNotification("手動排點成功！", "success");
        onClose();
    } catch (_err) {
        console.error("Error creating match:", _err);
        showNotification("建立對戰失敗。", "error");
    } finally {
        setLoading(false);
    }
  };

  const eligiblePlayers = allPlayers.filter(p => p.status !== 'on_court');
  
  if (loading) return <CircularProgress />;

  const getPlayerById = (id: number) => allPlayers.find(p => p.id === id);

  const renderTeamPreview = () => {
    if (selectedPlayerIds.length < 1) return null;

    const team1_p1 = getPlayerById(selectedPlayerIds[0])?.name || '...';
    const team1_p2 = getPlayerById(selectedPlayerIds[1])?.name || '...';
    const team2_p1 = getPlayerById(selectedPlayerIds[2])?.name || '...';
    const team2_p2 = getPlayerById(selectedPlayerIds[3])?.name || '...';
    
    return (
        <Box>
            <Typography variant="subtitle1"><strong>隊伍 A:</strong> {team1_p1} & {team1_p2}</Typography>
            <Typography variant="subtitle1"><strong>隊伍 B:</strong> {team2_p1} & {team2_p2}</Typography>
        </Box>
    )
  };

  return (
    <Box sx={{p: 2, minWidth: 400}}>
      <Typography variant="h6" gutterBottom>選擇 4 位球員</Typography>
      {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
      <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', maxHeight: '300px', overflowY: 'auto' }}>
        {eligiblePlayers.map(player => {
          const selectionIndex = selectedPlayerIds.indexOf(player.id);
          const isSelected = selectionIndex > -1;
          const label = isSelected ? `${selectionIndex + 1}. ${player.name}` : player.name;

          return (
            <FormControlLabel 
              key={player.id}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => handlePlayerSelection(player.id)}
                />
              } 
              label={label} 
            />
          );
        })}
      </FormGroup>
      
      <Divider sx={{my: 3}} />

      <FormControl fullWidth sx={{mb: 2}}>
        <InputLabel id="court-select-label">選擇一個可用場地</InputLabel>
        <Select
            labelId="court-select-label"
            value={selectedCourtId}
            label="選擇一個可用場地"
            onChange={(e) => setSelectedCourtId(e.target.value)}
        >
          {availableCourts.map(court => (
            <MenuItem key={court.id} value={court.id}>{court.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedPlayerIds.length === 4 && (
        <Paper elevation={1} sx={{p: 2, my: 2, backgroundColor: 'action.hover'}}>
            <Typography variant="h6" gutterBottom>對戰組合預覽</Typography>
            {renderTeamPreview()}
        </Paper>
      )}

      <Box mt={2}>
        <Button 
          variant="contained"
          onClick={handleCreateMatch} 
          disabled={selectedPlayerIds.length !== 4 || !selectedCourtId || loading}
          fullWidth
        >
          建立對戰
        </Button>
      </Box>
    </Box>
  );
};

export default ManualScheduler;